/**
 * Types and functions for 3d planes
 * @module
 */
import type { Slope } from "common/types/nominalNumbersTypes";
import { slopeNew } from "common/utils/nominalNumbers";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "./core";
import type { Line3Segment } from "./line3";
import { line3Tangent } from "./line3";
import type { Mat4 } from "./mat4";
import { mat4Dot, mat4DotSub3, mat4Rotate } from "./mat4";
import type { Plane } from "./planeTypes";
import { vec2Normal } from "./polyclip/vec2";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3Edge, polyLine3EdgeCount, polyLine3New, polyLine3OnPlane } from "./polyLine3";
import { solveLinearEquationSystem } from "./solveLinearEquationSystem";
import type { Vec2 } from "./vec2";
import { vec2Result } from "./vec2";
import type { Vec3 } from "./vec3";
import {
  vec3AngleBetween,
  vec3Cross,
  vec3CrossNormalize,
  vec3Dist,
  vec3DistSquared,
  vec3Dot,
  vec3Normalize,
  vec3Perpendicular,
  vec3Result,
  vec3Sub,
} from "./vec3";

// Re-export Plane type for backwards compatibility
export type { Plane } from "./planeTypes";

const { abs } = Math;

export function planeNewXY(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, 1.0, -0, -0, -0, 1.0, -0);
}

export function planeNewXYNeg(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, 1.0, -0, -0, -0, -1.0, -0);
}

export function planeNewXZ(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, 1.0, -0, -0, -0, -0, 1.0);
}

export function planeNewXZNeg(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, -1.0, -0, -0, -0, -0, 1.0);
}

export function planeNewYZ(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, 0, 1.0, -0, -0, -0, 1.0);
}

export function planeNewYZNeg(originX: number = -0, originY: number = -0, originZ: number = -0): Plane {
  return planeNew(originX, originY, originZ, 0, -1.0, -0, -0, -0, 1.0);
}

// Returns a plane given 2 points. The U and V directions are underconstrained.
export function planeByTwoPoints(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): Plane | null {
  const length = vec3Dist(x0, y0, z0, x1, y1, z1);

  if (length < DIST_EPSILON) {
    return null;
  }

  // get the tangent of the line
  const invlen = 1 / length;
  const uX = (x1 - x0) * invlen;
  const uY = (y1 - y0) * invlen;
  const uZ = (z1 - z0) * invlen;

  // get the plane normal
  const [nX, nY, nZ] = vec3Dot(uX, uY, uZ, 0, 0, 1) > ONE_MINUS_DIST_EPSILON ? [1, 0, 0] : [0, 0, 1];

  // get a vector perpendicular to the line
  const [vX, vY, vZ] = vec3CrossNormalize(uX, uY, uZ, nX, nY, nZ);

  return planeNew(x0, y0, z0, uX, uY, uZ, vX, vY, vZ);
}

// planeByThreePoints forms a plane by the following logic:
// - the first point is the plane origin
// - the direction from the first to the second point is the u direction of the plane
// - the third point helps define the plane normal
// - the v direction is formed by finding the vector mutually perpendicular to u and the normal using the cross product
export function planeByThreePoints(
  originX: number,
  originY: number,
  originZ: number,
  aX: number,
  aY: number,
  aZ: number,
  bX: number,
  bY: number,
  bZ: number,
): Plane | null {
  const [uX, uY, uZ] = line3Tangent(originX, originY, originZ, aX, aY, aZ);
  const [heightdirx, heightdiry, heightdirz] = line3Tangent(originX, originY, originZ, bX, bY, bZ);

  let [fnormx, fnormy, fnormz] = vec3Cross(uX, uY, uZ, heightdirx, heightdiry, heightdirz);

  [fnormx, fnormy, fnormz] = vec3Normalize(fnormx, fnormy, fnormz);

  if (isNaN(fnormx)) {
    return null;
  }

  const [vX, vY, vZ] = vec3Cross(fnormx, fnormy, fnormz, uX, uY, uZ);
  return planeNew(originX, originY, originZ, uX, uY, uZ, vX, vY, vZ);
}

/**
 * Solve a plane equation from an UV mapped triangle, so that the UV coordinate system matches the mapping on the triangle as closely as possible.
 * If the UV mapping is not an affine transformation, the resulting plane doesn't have exactly the same UVs.
 */
export function planeByThreePointsWithUv(x0, y0, z0, x1, y1, z1, x2, y2, z2, u0, v0, u1, v1, u2, v2): Plane {
  // The notation in this comment uses ; to separate matrix rows. So [x; y] is a column vector.
  //
  // We solve the plane equation from the UV mapping of the triangle:
  //
  // Xi = [ui vi 1  0  0  0  0  0  0;
  //       0  0  0  ui vi 1  0  0  0;
  //       0  0  0  0  0  0  ui vi 1]
  //
  // [x0; y0; z0; x1; y1; z1; x2; y2; z2] = [X0; X1; X2] * [uX; vX; originX; uY; vY; originY; uZ; vZ; originZ]

  const u = [u0, u1, u2];
  const v = [v0, v1, v2];
  const X: number[][] = [];
  for (let i = 0; i < 3; i++) {
    X.push([u[i], v[i], 1, 0, 0, 0, 0, 0, 0]);
    X.push([0, 0, 0, u[i], v[i], 1, 0, 0, 0]);
    X.push([0, 0, 0, 0, 0, 0, u[i], v[i], 1]);
  }
  const [uX, vX, originX, uY, vY, originY, uZ, vZ, originZ] = solveLinearEquationSystem(X, [
    x0,
    y0,
    z0,
    x1,
    y1,
    z1,
    x2,
    y2,
    z2,
  ]);
  return planeNew(originX, originY, originZ, uX, uY, uZ, vX, vY, vZ);
}

// Returns a plane given a location and a normal. The U and V directions are underconstrained.
export function planeByNormal(x0, y0, z0, nx, ny, nz): Plane {
  // b is an arbitrary vector not parallel to n.
  let [bx, by, bz] = [0, 0, 1];
  if (vec3Dot(bx, by, bz, nx, ny, nz)) {
    [bx, by, bz] = [0, 1, 0];
  }
  let [vx, vy, vz] = vec3Cross(nx, ny, nz, bx, by, bz);
  let [ux, uy, uz] = vec3Cross(vx, vy, vz, nx, ny, nz);
  [ux, uy, uz] = vec3Normalize(ux, uy, uz);
  [vx, vy, vz] = vec3Normalize(vx, vy, vz);
  return planeNew(x0, y0, z0, ux, uy, uz, vx, vy, vz);
}

export function planeReverse(pl: Plane) {
  pl.uX *= -1;
  pl.uY *= -1;
  pl.uZ *= -1;
  return pl;
}

export function planeNew(
  originX: number = -0,
  originY: number = -0,
  originZ: number = -0,
  uX: number = -0,
  uY: number = -0,
  uZ: number = -0,
  vX: number = -0,
  vY: number = -0,
  vZ: number = -0,
): Plane {
  return {
    originX,
    originY,
    originZ,
    uX,
    uY,
    uZ,
    vX,
    vY,
    vZ,
  };
}

export function planeEval({ originX, originY, originZ, uX, uY, uZ, vX, vY, vZ }: Plane, u: number, v: number) {
  vec3Result[0] = originX + uX * u + vX * v;
  vec3Result[1] = originY + uY * u + vY * v;
  vec3Result[2] = originZ + uZ * u + vZ * v;

  return vec3Result;
}

export function planeEvalDir({ uX, uY, uZ, vX, vY, vZ }: Plane, u: number, v: number) {
  vec3Result[0] = uX * u + vX * v;
  vec3Result[1] = uY * u + vY * v;
  vec3Result[2] = uZ * u + vZ * v;

  return vec3Result;
}

export function planeNormal(p: Plane): Vec3 {
  const { uX, uY, uZ, vX, vY, vZ } = p;
  return vec3Cross(uX, uY, uZ, vX, vY, vZ);
}

export function planeCopy(plane: Plane): Plane {
  const p = planeNew();
  return Object.assign(p, plane);
}

export function planeCopyReversed(plane: Plane): Plane {
  return planeReverse(planeCopy(plane));
}

export function planeCopyMoved(plane: Plane, x: number, y: number, z: number): Plane {
  return planeMove(planeCopy(plane), x, y, z);
}

export function planeMoveAlongNormal(plane: Plane, dist: number) {
  const [nx, ny, nz] = planeNormal(plane);
  return planeMove(plane, dist * nx, dist * ny, dist * nz);
}

export function planeCopyMoveAlongNormal(plane: Plane, dist: number) {
  return planeMoveAlongNormal(planeCopy(plane), dist);
}

export function planeMove(plane: Plane, x: number, y: number, z: number): Plane {
  plane.originX += x;
  plane.originY += y;
  plane.originZ += z;
  return plane;
}

export function planeProject(plane: Plane, x: number, y: number, z: number): Vec2 {
  const [dx, dy, dz] = vec3Sub(x, y, z, plane.originX, plane.originY, plane.originZ);

  vec2Result[0] = vec3Dot(dx, dy, dz, plane.uX, plane.uY, plane.uZ);
  vec2Result[1] = vec3Dot(dx, dy, dz, plane.vX, plane.vY, plane.vZ);

  return vec2Result;
}

export function planeProjectVec3(plane: Plane, x: number, y: number, z: number): Vec2 {
  vec2Result[0] = vec3Dot(x, y, z, plane.uX, plane.uY, plane.uZ);
  vec2Result[1] = vec3Dot(x, y, z, plane.vX, plane.vY, plane.vZ);

  return vec2Result;
}

export function planeProjectIntoTargetPlane(source: Plane, target: Plane): number[] {
  // use target plane orthonormal basis vectors to transform source plane origin and normal into target space
  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = target;
  const [nX, nY, nZ] = planeNormal(target);

  // origin * inverse(T)
  const [dx, dy, dz] = vec3Sub(source.originX, source.originY, source.originZ, originX, originY, originZ);

  // origin * inverse(T) * inverse(R)
  const x = vec3Dot(uX, uY, uZ, dx, dy, dz);
  const y = vec3Dot(vX, vY, vZ, dx, dy, dz);
  const z = vec3Dot(nX, nY, nZ, dx, dy, dz);

  // normal * inverse(R)
  const n = planeNormal(source);
  const a = vec3Dot(uX, uY, uZ, n[0], n[1], n[2]);
  const b = vec3Dot(vX, vY, vZ, n[0], n[1], n[2]);
  const c = vec3Dot(nX, nY, nZ, n[0], n[1], n[2]);
  const d = a * x + b * y + c * z;

  return [a, b, c, d]; // plane equation
}

export function planeProjectPolyLine3(plane: Plane, pl: PolyLine3): PolyLine2 {
  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = plane;

  const p2 = polyLine2New((pl.length / 3) * 2);

  for (let i = 0, j = 0, li = pl.length; i < li; j += 2, i += 3) {
    const dx = pl[i] - originX;
    const dy = pl[i + 1] - originY;
    const dz = pl[i + 2] - originZ;

    p2[j] = dx * uX + dy * uY + dz * uZ;
    p2[j + 1] = dx * vX + dy * vY + dz * vZ;
  }

  return p2;
}

export function polyLine2OnPlane(plane: Plane, pl: PolyLine2): PolyLine3 {
  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = plane;

  const p3 = polyLine3New((pl.length / 2) * 3);

  for (let i = 0, l = pl.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    const u = pl[i2];
    const v = pl[i2 + 1];

    p3[i3] = originX + uX * u + vX * v;
    p3[i3 + 1] = originY + uY * u + vY * v;
    p3[i3 + 2] = originZ + uZ * u + vZ * v;
  }

  return p3;
}

export function planeReProjectPolyLine2(pl: PolyLine2, srcPlane: Plane, dstPlane: Plane): PolyLine2 {
  const p3 = polyLine3OnPlane(pl, srcPlane);
  return planeProjectPolyLine3(dstPlane, p3);
}

export function planeProject3(plane: Plane, x: number, y: number, z: number): Vec3 {
  const [u, v] = planeProject(plane, x, y, z);
  return planeEval(plane, u, v);
}

export function planeDistanceTo(plane: Plane, x: number, y: number, z: number): number {
  const [px, py, pz] = planeProject3(plane, x, y, z);
  return vec3Dist(x, y, z, px, py, pz);
}

export function planeSignedDistanceTo(plane: Plane, x: number, y: number, z: number): number {
  const { originX, originY, originZ } = plane;
  const [nx, ny, nz] = planeNormal(plane);

  return vec3Dot(nx, ny, nz, x - originX, y - originY, z - originZ);
}

export function planeIsParallel(plane0: Plane, plane1: Plane, normal_angle_tol: number = DIST_EPSILON): boolean {
  const [nx0, ny0, nz0] = planeNormal(plane0);
  const [nx1, ny1, nz1] = planeNormal(plane1);

  const dot = vec3Dot(nx0, ny0, nz0, nx1, ny1, nz1);
  return abs(abs(dot) - 1) < normal_angle_tol;
}

export function planeIsHorizontal(plane: Plane, normal_angle_tol: number = ANGLE_EPSILON) {
  const [nx, ny, nz] = planeNormal(plane);
  const dot = vec3Dot(nx, ny, nz, 0, 0, 1);
  return abs(dot) > 1 - normal_angle_tol;
}

export function planeIsVertical(plane: Plane, normal_angle_tol: number = DIST_EPSILON) {
  const [, , z] = planeNormal(plane);
  return abs(z) < normal_angle_tol;
}

export function planeIsCodirectional(plane0: Plane, plane1: Plane, normal_angle_tol: number = ANGLE_EPSILON): boolean {
  const [n0X, n0Y, n0Z] = planeNormal(plane0);
  const [n1X, n1Y, n1Z] = planeNormal(plane1);

  return vec3AngleBetween(n0X, n0Y, n0Z, n1X, n1Y, n1Z) < normal_angle_tol;
}

export function planeIsCoplanar(
  plane0: Plane,
  plane1: Plane,
  dist_tol: number = DIST_EPSILON,
  normal_angle_tol: number = DIST_EPSILON,
): boolean {
  const [px, py, pz] = planeProject3(plane0, plane1.originX, plane1.originY, plane1.originZ);
  const dist = vec3Dist(px, py, pz, plane1.originX, plane1.originY, plane1.originZ);

  if (dist > dist_tol) {
    return false;
  }

  return planeIsParallel(plane0, plane1, normal_angle_tol);
}

export function planeIsPerpendicular(plane0: Plane, plane1: Plane, normal_angle_tol: number = ANGLE_EPSILON): boolean {
  return vec3Perpendicular(planeNormal(plane0), planeNormal(plane1), normal_angle_tol);
}

export function planeIsOrientedCoplanar(
  plane0: Plane,
  plane1: Plane,
  dist_tol: number = DIST_EPSILON,
  normal_angle_tol: number = DIST_EPSILON,
): boolean {
  if (!planeIsCoplanar(plane0, plane1, dist_tol, normal_angle_tol)) {
    return false;
  }

  return planeIsCodirectional(plane0, plane1);
}

export function planeIsOrientedCoplanarUvAligned(
  plane0: Plane,
  plane1: Plane,
  dist_tol: number = DIST_EPSILON,
  normal_angle_tol: number = DIST_EPSILON,
  uv_angle_tol: number = ANGLE_EPSILON,
) {
  if (!planeIsOrientedCoplanar(plane0, plane1, dist_tol, normal_angle_tol)) {
    return false;
  }

  // The UV frame cannot be assumed to be orthogonal or orthonormal , so we must compare
  // U and V vectors independently
  const [nu0X, nu0Y, nu0Z] = vec3Normalize(plane0.uX, plane0.uY, plane0.uZ);
  const [nu1X, nu1Y, nu1Z] = vec3Normalize(plane1.uX, plane1.uY, plane1.uZ);
  const uDot = vec3Dot(nu0X, nu0Y, nu0Z, nu1X, nu1Y, nu1Z);
  if (1 - uDot >= uv_angle_tol) {
    return false;
  }

  const [nv0X, nv0Y, nv0Z] = vec3Normalize(plane0.vX, plane0.vY, plane0.vZ);
  const [nv1X, nv1Y, nv1Z] = vec3Normalize(plane1.vX, plane1.vY, plane1.vZ);
  const vDot = vec3Dot(nv0X, nv0Y, nv0Z, nv1X, nv1Y, nv1Z);
  return 1 - vDot < uv_angle_tol;
}

export enum PlaneSide {
  FRONT,
  BACK,
  COPLANAR,
  INTERSECTS,
}

export function vec3PlaneSide(
  x: number,
  y: number,
  z: number,
  originX: number,
  originY: number,
  originZ: number,
  normX: number,
  normY: number,
  normZ: number,
  tol: number = DIST_EPSILON,
): PlaneSide {
  const d = vec3Dot(normX, normY, normZ, x - originX, y - originY, z - originZ);

  if (d > tol) {
    return PlaneSide.FRONT;
  }

  if (d < -tol) {
    return PlaneSide.BACK;
  }

  return PlaneSide.COPLANAR;
}

export function planeContainsVec3(
  plane: Plane,
  x: number,
  y: number,
  z: number,
  tolerance: number = DIST_EPSILON,
): boolean {
  const [nx, ny, nz] = planeNormal(plane);
  const [dx, dy, dz] = vec3Sub(x, y, z, plane.originX, plane.originY, plane.originZ);
  return abs(vec3Dot(nx, ny, nz, dx, dy, dz)) < tolerance;
}

export function planeContainsLine3(plane: Plane, line: Line3Segment, tolerance: number = DIST_EPSILON): boolean {
  return (
    planeContainsVec3(plane, line[0], line[1], line[2], tolerance) &&
    planeContainsVec3(plane, line[3], line[4], line[5], tolerance)
  );
}

/**
 * @returns indices of the edges that can be considered to be on the plane within tolerance
 */
export function planeContainsPolyLine3Edges(plane: Plane, pl3: PolyLine3, tolerance: number = DIST_EPSILON): number[] {
  const numEdges = polyLine3EdgeCount(pl3);
  const edgesOnPlane: number[] = [];
  for (let i = 0; i < numEdges; i++) {
    if (planeContainsLine3(plane, polyLine3Edge(pl3, i) as unknown as Line3Segment, tolerance)) {
      edgesOnPlane.push(i);
    }
  }
  return edgesOnPlane;
}

// calculates a downward vector for the slope against the global up vector (useful for determining slope)
export function planeNormalizedDownwardSlopeVector(plane: Plane): Vec3 | undefined {
  const rawNormal = planeNormal(plane);
  // horizontal plane or plane with degenerate normal
  if (Math.abs(rawNormal[0]) < DIST_EPSILON && Math.abs(rawNormal[1]) < DIST_EPSILON) {
    return undefined;
  }
  const [normalX, normalY, normalZ] = vec3Normalize(rawNormal[0], rawNormal[1], rawNormal[2]);
  const acrossSlope = vec3Cross(0, 0, 1, normalX, normalY, normalZ);
  const downSlopeVec = vec3Cross(acrossSlope[0], acrossSlope[1], acrossSlope[2], normalX, normalY, normalZ);
  return vec3Normalize(downSlopeVec[0], downSlopeVec[1], downSlopeVec[2]);
}

export function planeRotateAroundU(plane: Plane, radians: number) {
  const { uX, uY, uZ, vX, vY, vZ } = plane;

  const cosTheta = Math.cos(radians);
  const sinTheta = Math.sin(radians);

  const [bX, bY, bZ] = vec3Cross(uX * sinTheta, uY * sinTheta, uZ * sinTheta, vX, vY, vZ);

  plane.vX = vX * cosTheta + bX;
  plane.vY = vY * cosTheta + bY;
  plane.vZ = vZ * cosTheta + bZ;

  return plane;
}

export function planeRotateAroundV(plane: Plane, radians: number) {
  const { uX, uY, uZ, vX, vY, vZ } = plane;

  const cosTheta = Math.cos(radians);
  const sinTheta = Math.sin(radians);

  const [bX, bY, bZ] = vec3Cross(uX, uY, uZ, vX * sinTheta, vY * sinTheta, vZ * sinTheta);

  plane.uX = uX * cosTheta + bX;
  plane.uY = uY * cosTheta + bY;
  plane.uZ = uZ * cosTheta + bZ;

  return plane;
}

export function planeRotateAroundNormal(plane: Plane, radians: number) {
  const { uX, uY, uZ, vX, vY, vZ } = plane;
  const [nX, nY, nZ] = planeNormal(plane);

  const mat = mat4Rotate(radians, nX, nY, nZ);

  if (mat) {
    [plane.uX, plane.uY, plane.uZ] = mat4Dot(mat, uX, uY, uZ);
    [plane.vX, plane.vY, plane.vZ] = mat4Dot(mat, vX, vY, vZ);
  }

  return plane;
}

export function planeRotateAroundVector(plane: Plane, radians: number, vector: Vec3) {
  const { uX, uY, uZ, vX, vY, vZ } = plane;
  const [nx, ny, nz] = vec3Normalize(vector[0], vector[1], vector[2]);
  const mat = mat4Rotate(radians, nx, ny, nz);

  if (mat) {
    [plane.uX, plane.uY, plane.uZ] = mat4Dot(mat, uX, uY, uZ);
    [plane.vX, plane.vY, plane.vZ] = mat4Dot(mat, vX, vY, vZ);
  }

  return plane;
}

export function planeNaN(plane: Plane) {
  return isNaN(
    plane.originX + plane.originY + plane.originZ + plane.uX + plane.uY + plane.uZ + plane.vX + plane.vY + plane.vZ,
  );
}

export function planeFixNaN(plane: Plane) {
  if (isNaN(plane.originX)) {
    plane.originX = 0;
  }
  if (isNaN(plane.originY)) {
    plane.originY = 0;
  }
  if (isNaN(plane.originZ)) {
    plane.originZ = 0;
  }
  if (isNaN(plane.uX)) {
    plane.uX = 0;
  }
  if (isNaN(plane.uY)) {
    plane.uY = 0;
  }
  if (isNaN(plane.uZ)) {
    plane.uZ = 0;
  }
  if (isNaN(plane.vX)) {
    plane.vX = 0;
  }
  if (isNaN(plane.vY)) {
    plane.vY = 0;
  }
  if (isNaN(plane.vZ)) {
    plane.vZ = 0;
  }
  return plane;
}

export function planeCompare(a: Plane, b: Plane): boolean {
  return (
    a.originX === b.originX &&
    a.originY === b.originY &&
    a.originZ === b.originZ &&
    a.uX === b.uX &&
    a.uY === b.uY &&
    a.uZ === b.uZ &&
    a.vX === b.vX &&
    a.vY === b.vY &&
    a.vZ === b.vZ
  );
}

export function planeCompareApproximate(
  a: Plane,
  b: Plane,
  distSquaredTolerance: number = DIST_EPSILON_SQUARED,
): boolean {
  if (vec3DistSquared(a.originX, a.originY, a.originZ, b.originX, b.originY, b.originZ) > distSquaredTolerance) {
    return false;
  }
  if (vec3DistSquared(a.uX, a.uY, a.uZ, b.uX, b.uY, b.uZ) > distSquaredTolerance) {
    return false;
  }
  if (vec3DistSquared(a.vX, a.vY, a.vZ, b.vX, b.vY, b.vZ) > distSquaredTolerance) {
    return false;
  }
  return true;
}

export function planeTransform({ originX, originY, originZ, uX, uY, uZ, vX, vY, vZ }: Plane, mat: Mat4): Plane {
  const [newOriginX, newOriginY, newOriginZ] = mat4Dot(mat, originX, originY, originZ);
  const [newUX, newUY, newUZ] = mat4DotSub3(mat, uX, uY, uZ);
  const [newVX, newVY, newVZ] = mat4DotSub3(mat, vX, vY, vZ);

  return planeNew(newOriginX, newOriginY, newOriginZ, newUX, newUY, newUZ, newVX, newVY, newVZ);
}

export function planeXYAtZ(z: number): Plane {
  const plane = planeByThreePoints(0, 0, z, 1, 0, z, 0, 1, z);
  if (plane === null) {
    throw new Error(`planeXYAtZ at [${z}] could not be created`);
  }
  return plane;
}

export function planeIsNotNull(plane: Plane | null): plane is Plane {
  return plane !== null;
}

/**
 * By default, the slope is calculated against the global up vector (0, 0, 1).
 */
export function planeSlope(p: Plane, vx: number = 0, vy: number = 0, vz: number = 1, epsilon = DIST_EPSILON): Slope {
  const [nx, ny, nz] = planeNormal(p);

  const angle = Math.PI / 2 - vec3AngleBetween(vx, vy, vz, nx, ny, nz);

  const run = Math.tan(angle);
  if (Math.abs(run) < epsilon) {
    return slopeNew(Infinity); // vertical slope
  }

  const slope = 1 / run;

  if (Math.abs(slope) < epsilon) {
    return slopeNew(0); // horizontal slope
  }

  return slopeNew(slope);
}

/**
 * Returns a canonical plane coplanar with refPlane. Same infinite plane produces the same
 * canonical form for consistent hashing and coordinate transforms.
 *
 * Uses a single formulation: origin at closest point to (0,0,0), U from projecting
 * (1,0,0) onto the plane (or (0,1,0) if degenerate), V = n × U. The normal is
 * canonicalized so opposite-facing planes (same infinite plane) produce the same result. Opposite normals
 * (same infinite plane, opposite-facing) produce the same canonical plane.
 *
 * @param refPlane - Any plane to canonicalize
 * @returns Canonical plane coplanar with input, or null if degenerate
 */
export function planeCanonicalFromRefPlane(refPlane: Plane): Plane | null {
  let [nx, ny, nz] = planeNormal(refPlane);

  // Canonicalize normal so opposite-facing planes (same infinite plane) produce the same result.
  // Use first non-zero component (nz, ny, nx) to pick orientation: prefer positive.
  if (Math.abs(nz) > DIST_EPSILON) {
    if (nz < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
  } else if (Math.abs(ny) > DIST_EPSILON) {
    if (ny < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
  } else if (nx < 0) {
    nx = -nx;
    ny = -ny;
    nz = -nz;
  }

  const nLenSq = nx * nx + ny * ny + nz * nz;
  if (nLenSq < DIST_EPSILON * DIST_EPSILON) {
    return null; // Degenerate normal (U × V ≈ 0)
  }

  // Origin: closest point on the plane to (0,0,0). From plane equation n·p = d,
  // the closest point is (d/|n|²)*n.
  const d = vec3Dot(nx, ny, nz, refPlane.originX, refPlane.originY, refPlane.originZ);
  const t = d / nLenSq;
  const [ox, oy, oz] = [t * nx, t * ny, t * nz];

  // U: projection of (1,0,0) onto the plane (component perpendicular to n).
  // If plane is YZ (n ≈ ±X), that projection is degenerate; use (0,1,0) instead.
  let uX = 1 - (nx * nx) / nLenSq;
  let uY = -(nx * ny) / nLenSq;
  let uZ = -(nx * nz) / nLenSq;
  let uLen = Math.sqrt(uX * uX + uY * uY + uZ * uZ);
  if (uLen < DIST_EPSILON) {
    uX = -(ny * nx) / nLenSq;
    uY = 1 - (ny * ny) / nLenSq;
    uZ = -(ny * nz) / nLenSq;
    uLen = Math.sqrt(uX * uX + uY * uY + uZ * uZ);
  }
  if (uLen < DIST_EPSILON) {
    return null; // Both (1,0,0) and (0,1,0) projections degenerate; plane normal ~ (0,0,±1)
  }
  [uX, uY, uZ] = vec3Normalize(uX, uY, uZ);
  // V: n × U, normalized. Forms orthonormal basis (U, V, n) in the plane.
  let [vX, vY, vZ] = vec3CrossNormalize(nx, ny, nz, uX, uY, uZ);

  // For vertical planes (normal has near-zero z), canonicalize local +Y ("V")
  // to point world-up (+Z). Negating both U and V preserves the same plane normal.
  if (Math.abs(nz) <= DIST_EPSILON && vZ < 0) {
    uX = -uX;
    uY = -uY;
    uZ = -uZ;
    vX = -vX;
    vY = -vY;
    vZ = -vZ;
  }

  return planeNew(ox, oy, oz, uX, uY, uZ, vX, vY, vZ);
}

/**
 * produces the canonical cross plane for a segment in a plane
 * that is perpendicular to the segment
 */
export function planeCanonicalFromSegment(sx: number, sy: number, ex: number, ey: number, plane: Plane) {
  const [ox, oy, oz] = planeEval(plane, sx, sy);
  const [nu, nv] = vec2Normal(sx, sy, ex, ey);
  const [px, py, pz] = planeEvalDir(plane, nu, nv);

  const lenSq = px * px + py * py + pz * pz;
  if (lenSq < DIST_EPSILON * DIST_EPSILON) {
    return null;
  }

  const crossPlane = planeByNormal(ox, oy, oz, px, py, pz);
  const canonicalPlane = planeCanonicalFromRefPlane(crossPlane);
  if (!canonicalPlane) {
    return null;
  }

  return canonicalPlane;
}

/**
 * Computes a stable hash string for a plane based on its normal direction and distance from origin.
 * Coplanar planes (same infinite geometric plane) will produce the same hash.
 * Used to identify wall planes for associating overrides.
 */
export function planeHash(plane: Plane): string {
  const [nx, ny, nz] = planeNormal(plane);
  const angleX = cleanAngleForHash(vec3AngleBetween(nx, ny, nz, 1, 0, 0));
  const angleY = cleanAngleForHash(vec3AngleBetween(nx, ny, nz, 0, 1, 0));
  const distance = planeSignedDistanceTo(plane, 0, 0, 0);
  return `${angleX.toFixed(2)}-${angleY.toFixed(2)}-${distance.toFixed(2)}`;
}

/** Clean angle for hashing - normalize to [0, 2*PI) range */
function cleanAngleForHash(angle: number, tolerance = 0.00001): number {
  const TWO_PI = Math.PI * 2;
  angle = angle % TWO_PI;
  if (angle < 0) {
    angle += TWO_PI;
  }
  if (Math.abs(angle) < tolerance) {
    return 0;
  }
  if (Math.abs(angle - TWO_PI) < tolerance) {
    return 0;
  }
  return angle;
}
